import { useEffect, useState } from 'react';
import { fetchTenants } from '../services/tenantService';
import type { TenantSummary } from '../services/types';

/** Zustand der geladenen Mandantenliste. */
export interface TenantsState {
  /** Geladene Mandanten oder null (laedt noch bzw. Fehler). */
  readonly data: readonly TenantSummary[] | null;
  /** true, waehrend ein Request laeuft. */
  readonly loading: boolean;
  /** Deutsche Fehlermeldung oder null. */
  readonly error: string | null;
  /** Loest einen erneuten Ladeversuch aus (z. B. "Erneut laden"-Button). */
  reload(): void;
}

/** Fehlermeldung fuer fehlgeschlagene Mandanten-Requests. */
const LOAD_ERROR_MESSAGE = 'Die Mandantenliste konnte nicht geladen werden.';

/** Geladenes Ergebnis samt Generation (fuer den Stale-Guard bei reload()). */
interface LoadedTenants {
  readonly generation: number;
  readonly data: readonly TenantSummary[] | null;
  readonly error: string | null;
}

/**
 * Laedt die Liste aller Mandanten (kein Caching, Projektstandard).
 *
 * `reload()` erhoeht die Generation und loest damit einen neuen Request
 * aus — analog zum Stale-Guard in useUsers/useTimeSeries: Nur das
 * Ergebnis der aktuellen Generation wird uebernommen, eine noch
 * unterwegs befindliche vorherige Antwort wird verworfen.
 *
 * Fehler werden bewusst still behandelt (nur Fehlerzustand, KEIN
 * console.error) — analog den uebrigen Hooks.
 */
export function useTenants(): TenantsState {
  const [generation, setGeneration] = useState<number>(0);
  const [loaded, setLoaded] = useState<LoadedTenants | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoaded(null);
    fetchTenants(controller.signal)
      .then((response) => {
        if (controller.signal.aborted) {
          return;
        }
        setLoaded({ generation, data: response, error: null });
      })
      .catch(() => {
        if (controller.signal.aborted) {
          return;
        }
        setLoaded({ generation, data: null, error: LOAD_ERROR_MESSAGE });
      });
    return () => {
      controller.abort();
    };
  }, [generation]);

  const isCurrent = loaded !== null && loaded.generation === generation;
  return {
    data: isCurrent ? loaded.data : null,
    loading: !isCurrent,
    error: isCurrent ? loaded.error : null,
    reload: () => setGeneration((previous) => previous + 1),
  };
}

export default useTenants;
